/**
 * What a webview handed over when it was submitted, kept on the run — the
 * values the form passed to the bot (`PYPESTREAM_WEBVIEW_DATA_PASSTHROUGH`),
 * flattened into named fields a person can read and a judge can check.
 *
 * Runs are saved in the browser and sent to the scoring model, so a value
 * that could be a card, an account or a secret is masked here, before it is
 * kept anywhere: by its field name, and by its shape (a long run of digits).
 * Only the last four digits survive, which is enough to tell test values apart.
 */

export interface SubmittedField {
  /** Dotted path into the data: `incident-time.value`, `card.number`. */
  name: string;
  value: string;
  /** Replaced with a mask — the run never held the real value. */
  masked?: boolean;
}

export interface WebviewSubmission {
  /** The webview's name, as the bot labelled it. */
  form: string;
  fields: SubmittedField[];
}

const MAX_FIELDS = 30;
const MAX_VALUE = 200;

const SENSITIVE_NAME =
  /card|cvv|cvc|ccv|security.?code|exp(iry|iration)?.?(date|month|year)?$|ssn|social|tax.?id|account|acct|routing|iban|swift|sort.?code|pin\b|passw|passcode|secret|token|otp|dob|birth/i;

const mask = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 4 ? `•••• ${digits.slice(-4)}` : '••••';
};

/** A run of 9+ digits (spaces and dashes allowed) reads as a card or account. */
const looksLikeNumber = (value: string) =>
  /^[\d\s-]+$/.test(value) && value.replace(/\D/g, '').length >= 9;

const leaf = (name: string, raw: unknown): SubmittedField => {
  const value = String(raw ?? '').slice(0, MAX_VALUE);
  const last = name.split('.').pop() ?? name;
  if (
    SENSITIVE_NAME.test(last) ||
    SENSITIVE_NAME.test(name) ||
    looksLikeNumber(value)
  ) {
    return { name, value: mask(value), masked: true };
  }
  return { name, value };
};

/**
 * The submitted data as fields. Arrays of one object (the usual shape —
 * `[{ "incident-time": { … } }]`) don't add an index to every name; a
 * `{ name, value }` pair is read as one field.
 */
export const flattenSubmission = (data: unknown): SubmittedField[] => {
  const fields: SubmittedField[] = [];
  const isPair = (v: unknown): v is { name: string; value: unknown } =>
    !!v &&
    typeof v === 'object' &&
    typeof (v as { name?: unknown }).name === 'string' &&
    'value' in v &&
    Object.keys(v).length <= 2;
  const walk = (value: unknown, path: string) => {
    if (fields.length >= MAX_FIELDS) return;
    if (Array.isArray(value)) {
      // One item, or named pairs, already say which field is which.
      if (value.length === 1 || value.every(isPair)) {
        value.forEach((v) => walk(v, path));
      } else {
        value.forEach((v, i) =>
          walk(v, path ? `${path}.${i + 1}` : String(i + 1))
        );
      }
      return;
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      if (isPair(record)) {
        walk(record.value, path ? `${path}.${record.name}` : record.name);
        return;
      }
      Object.entries(record).forEach(([k, v]) =>
        walk(v, path ? `${path}.${k}` : k)
      );
      return;
    }
    fields.push(leaf(path || 'value', value));
  };
  walk(data, '');
  return fields;
};

/** Merge more data into what a form already handed over (a field sent twice keeps the last). */
export const addSubmission = (
  prev: WebviewSubmission | null,
  form: string,
  data: unknown
): WebviewSubmission => {
  const byName = new Map((prev?.fields ?? []).map((f) => [f.name, f]));
  flattenSubmission(data).forEach((f) => byName.set(f.name, f));
  return { form, fields: [...byName.values()].slice(0, MAX_FIELDS) };
};

/** One line for the judge: `Submitted Select a Time: incident-time.value = 13:30, …`. */
export const describeSubmission = (s: WebviewSubmission): string =>
  `Submitted ${s.form}: ${
    s.fields.length
      ? s.fields.map((f) => `${f.name} = ${f.value}`).join(', ')
      : 'no fields'
  }`;
